import type { PlatformStorage } from '@tbitop/platform';
import { getPlatformStorage } from '@tbitop/platform';
import type { KeycloakInstance } from 'keycloak-js';
import { isNull, isUndefined } from 'lodash-es';
import { log } from '../log';

export type Tokens = {
  idToken: string | undefined;
  accessToken: string | undefined;
  refreshToken: string | undefined;
};

const getStorage = () => getPlatformStorage();

export function createTokenStorage(url: string, realm: string, clientId: string) {
  return new TokenStorage(getStorage(), url, realm, clientId);
}

export function createTokenStorageForInstance(keycloakInstance: KeycloakInstance) {
  const { authServerUrl, realm, clientId } = keycloakInstance;

  if (isUndefined(authServerUrl) || isUndefined(realm) || isUndefined(clientId)) {
    throw new Error(`Cannot create token storage. Either authentication server URL (${authServerUrl}), realm (${realm}) or client ID (${clientId}) is missing.`);
  }
  return new TokenStorage(getStorage(), authServerUrl, realm, clientId);
}

export async function updateTokenStorage(tokenStorage: TokenStorage, keycloakInstance: KeycloakInstance) {
  if (keycloakInstance.authenticated) {
    log.debug('Updating token storage with latest tokens.');
    return tokenStorage.update({
      accessToken: keycloakInstance.token,
      idToken: keycloakInstance.idToken,
      refreshToken: keycloakInstance.refreshToken,
    });
  } else {
    log.debug('Removing tokens from storage.');
    return tokenStorage.remove();
  }
}

export class TokenStorage {

  private readonly tokenStorageKey: string;

  constructor(
    private storage: PlatformStorage,
    url: string,
    realm: string,
    clientId: string,
  ) {
    this.tokenStorageKey = `tbit-tokens-${url}-${realm}-${clientId}`;
  }

  async get(): Promise<Tokens | undefined> {
    const stringifiedTokens = await this.storage.get(this.tokenStorageKey);

    if (isNull(stringifiedTokens)) {
      return undefined;
    }

    try {
      return JSON.parse(stringifiedTokens);
    } catch (error) {
      log.warn(`Unable to parse stored tokens: ${stringifiedTokens}`, error);
      return undefined;
    }
  }

  async update(tokens: Tokens) {
    return this.storage.set(this.tokenStorageKey, JSON.stringify(tokens));
  }

  async remove() {
    return this.storage.remove(this.tokenStorageKey);
  }
}
